import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import UptimeSparkline from "@/components/UptimeSparkline";
import { toast } from "sonner";
import { Radio, Users, Crown, Music4, Loader2, Shield, DollarSign, Radio as LiveIcon, Heart, Play, Mail, Gift, Copy, Trash2, Check, KeyRound, History, AlertTriangle, Sliders, Eraser, Banknote, Send, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // Lifetime-user creation state
  const [compEmail, setCompEmail] = useState("");
  const [compName, setCompName] = useState("");
  const [compReason, setCompReason] = useState("Early tester");
  const [compBusy, setCompBusy] = useState(false);
  const [compCreated, setCompCreated] = useState(null);   // {email, password, upgraded}
  const [compedUsers, setCompedUsers] = useState([]);
  const [copiedKey, setCopiedKey] = useState("");
  const navigate = useNavigate();

  // Password reset (admin bypass — for when SMTP is broken or user can't get the reset email)
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {email, new_password}

  // Forgot-password delivery log + env diagnostic
  const [forgotLog, setForgotLog] = useState(null); // {attempts, env}
  const [forgotBusy, setForgotBusy] = useState(false);
  const [auditLog, setAuditLog] = useState(null); // {entries}
  const [auditBusy, setAuditBusy] = useState(false);
  const [alerts, setAlerts] = useState(null); // {alerts}
  const [midiTelemetry, setMidiTelemetry] = useState(null); // {pings, tally, total}
  const [midiTelemetryBusy, setMidiTelemetryBusy] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null); // {would_delete_count, preview}
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null); // {deleted_users, deleted_by_collection, users_remaining}

  // Pesapal payout dashboard state
  const [payoutsPending, setPayoutsPending] = useState(null); // {ready, below_threshold, totals}
  const [payoutsBusy, setPayoutsBusy] = useState(false);
  const [batchesHistory, setBatchesHistory] = useState([]);
  const [batchBusy, setBatchBusy] = useState(null); // null | "KES" | "UGX" | batch_id
  const [reminderBusy, setReminderBusy] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [uptime, setUptime] = useState({ status: "unknown", latency: null });
  // Last-hour rolling window of samples: {ts, latency, status}. Persisted to
  // localStorage so a tab refresh keeps recent history intact.
  const [samples, setSamples] = useState(() => {
    try {
      const raw = localStorage.getItem("nd_health_samples");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const cutoff = Date.now() - 60 * 60 * 1000;
      return Array.isArray(parsed) ? parsed.filter(s => s.ts >= cutoff) : [];
    } catch { return []; }
  });

  const loadForgotLog = async () => {
    setForgotBusy(true);
    try {
      const r = await axios.get(`${API}/admin/forgot-log?limit=20`);
      setForgotLog(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load log");
    } finally { setForgotBusy(false); }
  };

  const loadAuditLog = async () => {
    setAuditBusy(true);
    try {
      const r = await axios.get(`${API}/admin/reset-audit?limit=50`);
      setAuditLog(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load audit log");
    } finally { setAuditBusy(false); }
  };

  const loadAlerts = async () => {
    try {
      const r = await axios.get(`${API}/admin/reset-alerts?limit=20`);
      setAlerts(r.data);
    } catch { /* silent — panel just stays hidden */ }
  };

  const loadMidiTelemetry = async () => {
    setMidiTelemetryBusy(true);
    try {
      const r = await axios.get(`${API}/admin/midi-telemetry?limit=30`);
      setMidiTelemetry(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load MIDI telemetry");
    } finally { setMidiTelemetryBusy(false); }
  };

  // ── Pesapal payout dashboard ────────────────────────────────────────
  const loadPayouts = async () => {
    setPayoutsBusy(true);
    try {
      const [pending, batches] = await Promise.all([
        axios.get(`${API}/admin/payouts/pending`),
        axios.get(`${API}/admin/payouts/batches`),
      ]);
      setPayoutsPending(pending.data);
      setBatchesHistory(batches.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load payouts");
    } finally { setPayoutsBusy(false); }
  };

  const createBatch = async (currency) => {
    setBatchBusy(currency);
    try {
      const r = await axios.post(`${API}/admin/payouts/batch`, { currency });
      // Trigger CSV download inline — browsers can't open a POST response
      // directly, so synthesise a blob href from the returned csv_text.
      const blob = new Blob([r.data.csv_text], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.data.batch_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Batch ${r.data.batch_id} · ${currency} ${r.data.total_dj_share_local.toLocaleString()} · CSV downloaded`);
      await loadPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch creation failed");
    } finally { setBatchBusy(null); }
  };

  const markBatchPaid = async (batchId) => {
    setBatchBusy(batchId);
    try {
      await axios.post(`${API}/admin/payouts/batch/${batchId}/mark-paid`, { note: "Marked paid via admin dashboard" });
      toast.success(`Batch ${batchId} marked paid`);
      await loadPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark-paid failed");
    } finally { setBatchBusy(null); }
  };

  const markBatchFailed = async (batchId) => {
    if (!window.confirm(`Roll back batch ${batchId}? The DJ tips will go back to pending and can be re-exported in a new batch.`)) return;
    setBatchBusy(batchId);
    try {
      await axios.post(`${API}/admin/payouts/batch/${batchId}/mark-failed`, { note: "Rolled back via admin dashboard" });
      toast.success(`Batch ${batchId} rolled back`);
      await loadPayouts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark-failed failed");
    } finally { setBatchBusy(null); }
  };

  const downloadBatchCsv = async (batchId) => {
    try {
      const r = await axios.get(`${API}/admin/payouts/batch/${batchId}/csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${batchId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const sendPayoutReminder = async () => {
    setReminderBusy(true);
    try {
      const r = await axios.post(`${API}/admin/payouts/send-reminder`);
      if (r.data.skipped_reason) {
        toast.info(`Reminder skipped — ${r.data.skipped_reason.replace(/_/g, " ")}`);
      } else {
        toast.success(`Reminder emailed · sent=${r.data.sent} · errors=${r.data.errors}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder send failed");
    } finally { setReminderBusy(false); }
  };

  const previewPurge = async () => {
    setPurgeBusy(true);
    setPurgeResult(null);
    try {
      const r = await axios.post(`${API}/admin/purge-test-accounts`, {});
      setPurgePreview(r.data);
      if (r.data.would_delete_count === 0) {
        toast.info("No test accounts match — the DB is already clean.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPurgeBusy(false); }
  };

  const confirmPurge = async () => {
    if (!purgePreview?.would_delete_count) return;
    const count = purgePreview.would_delete_count;
    if (!window.confirm(`Permanently delete ${count} test account${count === 1 ? "" : "s"}? This cascades across follows / mixes / transactions and cannot be undone.`)) return;
    setPurgeBusy(true);
    try {
      const r = await axios.post(`${API}/admin/purge-test-accounts`, { confirm: "PURGE" });
      setPurgeResult(r.data);
      setPurgePreview(null);
      toast.success(`Purged ${r.data.deleted_users} test account${r.data.deleted_users === 1 ? "" : "s"}. ${r.data.users_remaining} real users remain.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally { setPurgeBusy(false); }
  };

  const ackAlert = async (alertId) => {
    try {
      await axios.post(`${API}/admin/reset-alerts/${alertId}/ack`);
      loadAlerts();
      // Offer one-tap self-rotation in case this alert wasn't the admin's own action.
      toast.success("Alert acknowledged", {
        description: "Wasn't you? Rotate your own admin password right now.",
        action: {
          label: "Rotate MY password",
          onClick: async () => {
            try {
              await axios.post(`${API}/auth/self-force-rotate`);
              await refreshUser();
              toast.info("Password rotation required — the change form is open.");
            } catch (e) {
              toast.error(e.response?.data?.detail || "Could not start rotation");
            }
          },
        },
        duration: 10000,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not acknowledge");
    }
  };

  const doResetPassword = async () => {
    if (!resetEmail || !resetEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (!window.confirm(`Reset password for ${resetEmail}? Their current password will stop working immediately.`)) return;
    setResetBusy(true);
    setResetResult(null);
    try {
      const r = await axios.post(`${API}/admin/users/reset-password`, { email: resetEmail.trim().toLowerCase() });
      setResetResult(r.data);
      toast.success("Password reset — copy it now");
      setResetEmail("");
      loadAuditLog();
      loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };

  const loadComped = async () => {
    try {
      const r = await axios.get(`${API}/admin/users/comped`);
      setCompedUsers(r.data || []);
    } catch { /* silent — non-admin view already 403s at /admin/stats */ }
  };

  const createComp = async () => {
    if (!compEmail || !compEmail.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setCompBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users/comp`, {
        email: compEmail.trim().toLowerCase(),
        display_name: compName.trim(),
        reason: compReason.trim() || "friend",
      });
      setCompCreated(r.data);
      toast.success(r.data.upgraded ? "Existing user upgraded to lifetime PRO" : "Lifetime PRO account created");
      setCompEmail(""); setCompName(""); setCompReason("Early tester");
      loadComped();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create lifetime account");
    } finally { setCompBusy(false); }
  };

  const revokeComp = async (userId, email) => {
    if (!window.confirm(`Revoke lifetime PRO for ${email}? Their account stays, but downgrades to demo-tier.`)) return;
    try {
      await axios.delete(`${API}/admin/users/comp/${userId}`);
      toast.success("Lifetime revoked");
      loadComped();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not revoke");
    }
  };

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch { toast.error("Copy failed — long-press to select"); }
  };

  const sendTestEmail = async () => {
    if (!testTo || !testTo.includes("@")) {
      toast.error("Enter a valid email address (any provider works)");
      return;
    }
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/admin/email/test`, { to_email: testTo });
      if (r.data.ok) {
        toast.success(`Test email accepted for ${testTo}. Check that inbox in ~30 s.`);
      } else {
        toast.error(`SMTP rejected: ${r.data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally { setSendingTest(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    axios.get(`${API}/admin/stats`)
      .then(r => setStats(r.data))
      .catch(e => setError(e.response?.data?.detail || "Admin only"));
    loadComped();
    loadAlerts();
    loadMidiTelemetry();
    loadPayouts();

    // Scroll to #payouts anchor if the admin arrived from the weekly
    // reminder email link.
    if (window.location.hash === "#payouts") {
      setTimeout(() => {
        document.getElementById("payouts")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }

    // Post-deploy confetti — fires ONCE when the running backend build
    // is newer than the one this admin has already seen. Version stored
    // in localStorage so it's per-device not per-session.
    (async () => {
      try {
        const r = await axios.get(`${API}/version`);
        const v = r.data?.version || "";
        setServerVersion(v);
        const seen = localStorage.getItem("nd_seen_build") || "";
        if (v && v !== seen) {
          localStorage.setItem("nd_seen_build", v);
          if (seen) {
            // Only fire on true version bumps — skip on the very first visit
            // so a brand-new admin doesn't get a phantom "Deploy shipped" ping.
            confetti({ particleCount: 120, spread: 70, origin: { y: 0.3 }, colors: ["#00F0FF","#FF2A6D","#F59E0B","#10B981","#B6FF3C"] });
            toast.success(`🎉 Deploy shipped ${v}`, {
              description: "Fresh backend build is live. Any change you shipped is now running.",
              duration: 6000,
            });
          }
        }
      } catch { /* silent — never block admin dashboard on version fetch */ }
    })();

    // Uptime heartbeat — polls /api/health every 30 s. Colour of the dot
    // reflects latest ping: green (ok + fast), amber (ok + slow), red (fail).
    // Also appends each sample to the rolling last-hour window used by
    // the sparkline popover.
    let cancelled = false;
    const pushSample = (s) => setSamples(prev => {
      const cutoff = Date.now() - 60 * 60 * 1000;
      const next = [...prev.filter(x => x.ts >= cutoff), s];
      try { localStorage.setItem("nd_health_samples", JSON.stringify(next.slice(-240))); } catch { /* quota */ }
      return next;
    });
    const ping = async () => {
      const t0 = performance.now();
      try {
        const r = await axios.get(`${API}/health`, { timeout: 8000 });
        const latency = Math.round(performance.now() - t0);
        if (cancelled) return;
        let status = "up";
        if (!r.data?.ok) status = "down";
        else if (latency > 2000) status = "slow";
        setUptime({ status, latency });
        pushSample({ ts: Date.now(), latency, status });
      } catch {
        if (!cancelled) {
          setUptime({ status: "down", latency: null });
          pushSample({ ts: Date.now(), latency: null, status: "down" });
        }
      }
    };
    ping();
    const iv = setInterval(ping, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  if (error) return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-6">
      <div className="text-center space-y-4 max-w-md" data-testid="admin-forbidden">
        <Shield className="w-12 h-12 text-[#EF4444] mx-auto"/>
        <div className="font-display font-black text-3xl">Access denied</div>
        <div className="text-[#94A3B8]">This dashboard is restricted to the NeuralDeck platform admin.</div>
        <Link to="/studio" className="inline-block px-4 py-2 rounded bg-[#00F0FF] text-black font-black uppercase tracking-widest">Back to Studio</Link>
      </div>
    </div>
  );
  if (!stats) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]"/></div>;

  const cards = [
    { label: "Users", val: stats.users, icon: Users, color: "#00F0FF" },
    { label: "Premium", val: stats.premium, icon: Crown, color: "#FFC107" },
    { label: "On Trial", val: stats.trial, icon: Crown, color: "#FF8C00" },
    { label: "Sets Published", val: stats.published_sets, icon: Music4, color: "#00F0FF" },
    { label: "Total Plays", val: stats.total_plays, icon: Play, color: "#FF8C00" },
    { label: "Reactions", val: stats.reactions, icon: Heart, color: "#FF2A6D" },
    { label: "Live Now", val: stats.active_live_sessions, icon: LiveIcon, color: "#EF4444" },
    { label: "Paid Tx", val: stats.paid_transactions, icon: DollarSign, color: "#10B981" },
    { label: "Revenue", val: `$${(stats.revenue_cents / 100).toFixed(2)}`, icon: DollarSign, color: "#10B981" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="admin-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#EF4444] to-[#FF8C00] flex items-center justify-center"><Shield className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
            <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#EF4444]">DECK</span> · ADMIN</div>
          </Link>
          {serverVersion && (
            <span data-testid="admin-version-pill" title={`Build ${serverVersion} — running on the backend right now`}
                  className="ml-1 px-2 py-0.5 rounded-full border border-[#1E2638] bg-[#0B0E14] text-[10px] font-mono tracking-widest text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/60 transition-colors">
              {serverVersion}
            </span>
          )}
          <UptimeSparkline samples={samples} currentStatus={uptime.status} currentLatency={uptime.latency}/>
        </div>
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white">← Studio</Link>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#EF4444] mb-2 flex items-center gap-2"><Shield className="w-3 h-3"/> // Owner Dashboard</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Platform pulse</h1>
          <p className="text-[#94A3B8] mt-2">Real-time global stats for NeuralDeck. Visible only to platform owners.</p>
        </div>

        {/* Live alert center — surface security alerts so they aren't lost to email-spam */}
        {alerts?.alerts?.length > 0 && (
          <div className="rounded-xl border-2 border-[#EF4444] bg-[#EF4444]/10 p-6 space-y-3 animate-pulse" data-testid="admin-alert-center">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[#EF4444]"/>
              <div className="font-display font-black text-2xl text-[#EF4444]">Security alerts ({alerts.alerts.length})</div>
            </div>
            <div className="space-y-2">
              {alerts.alerts.slice(0, 5).map((a, i) => (
                <div key={a.id || i} data-testid={`admin-alert-row-${i}`} className="rounded-lg bg-[#0B0E14] border border-[#EF4444]/40 p-3 text-xs space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-mono text-white">
                      <span className="text-[#EF4444] font-black">RAPID RESETS</span>
                      {" · "}
                      <span className="text-[#00F0FF]">{a.by_admin_email}</span>
                      {" reset "}
                      <span className="font-black">{a.target_count}</span> users
                    </div>
                    <div className="text-[10px] font-mono text-[#94A3B8]">{new Date(a.sent_at).toLocaleString()}</div>
                  </div>
                  <div className="text-[10px] font-mono text-[#94A3B8]">
                    IP: {a.ip} · Targets: {(a.targets || []).slice(0, 3).join(", ")}{(a.targets?.length || 0) > 3 ? "…" : ""}
                  </div>
                  {a.id && (
                    <div className="pt-1">
                      <button type="button" data-testid={`admin-alert-ack-${i}`} onClick={() => ackAlert(a.id)}
                              className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-white border border-[#00F0FF]/40 hover:border-[#00F0FF] rounded px-2 py-1 transition-colors">
                        ✓ Acknowledge
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] font-mono text-[#94A3B8] italic">
              // Shown here so alerts land somewhere you actually look. Rotate admin passwords if any of these weren't you.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
          {cards.map(c => (
            <div key={c.label} className="rounded-xl border border-[#1E2638] bg-[#121620] p-5" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center gap-2 mb-2" style={{ color: c.color }}>
                <c.icon className="w-4 h-4"/>
                <span className="text-[10px] font-mono uppercase tracking-widest">{c.label}</span>
              </div>
              <div className="font-display font-black text-4xl">{c.val}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EF4444] mb-3">// Conversion</div>
          <div className="flex items-baseline gap-4">
            <div className="font-display font-black text-5xl text-[#FFC107]">
              {stats.users > 0 ? Math.round((stats.premium / stats.users) * 100) : 0}%
            </div>
            <div className="text-sm text-[#94A3B8]">of {stats.users} users are paid PRO ({stats.trial} on trial)</div>
          </div>
        </div>

        {/* ──────────────────────────── Pesapal Payouts ──────────────────────────── */}
        <div id="payouts" className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-5" data-testid="admin-payouts-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Banknote className="w-6 h-6 text-[#B6FF3C]"/>
              <div>
                <div className="font-display font-black text-2xl">Pesapal payouts</div>
                <div className="text-xs text-[#94A3B8] font-mono mt-0.5">Mobile-money DJ settlements · KES (M-Pesa) + UGX (MTN/Airtel)</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="admin-payouts-refresh" variant="outline" size="sm"
                onClick={loadPayouts} disabled={payoutsBusy}
                className="border-[#2A354B] text-[#94A3B8] hover:border-[#B6FF3C] hover:text-[#B6FF3C]">
                {payoutsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5 mr-2"/>}
                Refresh
              </Button>
              <Button data-testid="admin-payouts-send-reminder" variant="outline" size="sm"
                onClick={sendPayoutReminder} disabled={reminderBusy}
                className="border-[#2A354B] text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF]">
                {reminderBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-2"/>}
                Email reminder now
              </Button>
            </div>
          </div>

          {/* Totals per currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["KES", "UGX"].map(cur => {
              const t = payoutsPending?.totals?.[cur];
              const hasReady = t && t.dj_count > 0;
              return (
                <div key={cur}
                     data-testid={`admin-payouts-total-${cur}`}
                     className={`rounded-lg border p-4 ${hasReady ? "border-[#B6FF3C]/40 bg-[#B6FF3C]/5" : "border-[#1E2638] bg-[#0B0E14]"}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">{cur === "KES" ? "🇰🇪 Kenya" : "🇺🇬 Uganda"}</div>
                    <div className="text-[10px] font-mono text-[#94A3B8]">{t?.tip_count || 0} tips · {t?.dj_count || 0} DJs ready</div>
                  </div>
                  <div className="font-display font-black text-3xl text-white mb-3">
                    {cur} {(t?.dj_share || 0).toLocaleString()}
                  </div>
                  <Button data-testid={`admin-payouts-batch-${cur}`}
                    onClick={() => createBatch(cur)}
                    disabled={!hasReady || batchBusy === cur}
                    className="w-full bg-[#B6FF3C] text-black hover:bg-[#9FE62E] font-black uppercase tracking-widest text-xs disabled:opacity-40">
                    {batchBusy === cur ? <Loader2 className="w-4 h-4 animate-spin"/> : (
                      <><Download className="w-3.5 h-3.5 mr-2"/> Export batch CSV</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Ready DJs list */}
          {payoutsPending?.ready?.length > 0 && (
            <div className="rounded-lg border border-[#1E2638] bg-[#0B0E14] overflow-hidden">
              <div className="px-4 py-2 border-b border-[#1E2638] text-[10px] font-mono uppercase tracking-widest text-[#B6FF3C]">
                Ready for payout ({payoutsPending.ready.length})
              </div>
              <div className="divide-y divide-[#1E2638]">
                {payoutsPending.ready.map(r => (
                  <div key={`${r.dj_user_id}-${r.currency}`}
                       data-testid={`admin-payouts-ready-row-${r.dj_handle}`}
                       className="px-4 py-2.5 flex items-center justify-between gap-3 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="font-black truncate">{r.dj_display_name} <span className="text-[#94A3B8] font-mono font-normal">· @{r.dj_handle}</span></div>
                      <div className="text-[10px] text-[#94A3B8] font-mono truncate">
                        {r.mobile_money_name} · {r.mobile_money_number} · {r.mobile_money_provider || "mpesa"}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-black text-white">{r.currency} {r.dj_share_local.toLocaleString()}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono">{r.tip_count} tips</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Below threshold */}
          {payoutsPending?.below_threshold?.length > 0 && (
            <details className="rounded-lg border border-[#1E2638] bg-[#0B0E14]">
              <summary className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFC107] cursor-pointer">
                Below min-payout threshold ({payoutsPending.below_threshold.length}) — accruing until next batch
              </summary>
              <div className="divide-y divide-[#1E2638]">
                {payoutsPending.below_threshold.map(r => (
                  <div key={`${r.dj_user_id}-${r.currency}-bt`}
                       className="px-4 py-2 flex items-center justify-between gap-3 text-xs">
                    <div className="min-w-0 truncate">
                      <span className="font-black">@{r.dj_handle}</span>
                      <span className="text-[#94A3B8] font-mono ml-2">needs {r.currency} {r.min_threshold.toLocaleString()}</span>
                    </div>
                    <div className="text-[#94A3B8] font-mono text-[10px] whitespace-nowrap">
                      {r.currency} {r.dj_share_local.toLocaleString()} · {r.tip_count} tips
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Empty state */}
          {payoutsPending && payoutsPending.ready?.length === 0 && payoutsPending.below_threshold?.length === 0 && (
            <div className="rounded-lg border border-[#1E2638] bg-[#0B0E14] p-4 text-center text-xs text-[#94A3B8] font-mono">
              No pending Pesapal payouts. All settled DJs are up to date.
            </div>
          )}

          {/* Batch history */}
          {batchesHistory.length > 0 && (
            <div className="rounded-lg border border-[#1E2638] bg-[#0B0E14] overflow-hidden">
              <div className="px-4 py-2 border-b border-[#1E2638] text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">
                Batch history ({batchesHistory.length})
              </div>
              <div className="divide-y divide-[#1E2638] max-h-[320px] overflow-y-auto">
                {batchesHistory.map(b => (
                  <div key={b.batch_id}
                       data-testid={`admin-payouts-batch-row-${b.batch_id}`}
                       className="px-4 py-2.5 flex items-center justify-between gap-3 text-xs flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-white truncate">{b.batch_id}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono">
                        {new Date(b.created_at).toLocaleString()} · {b.dj_count} DJs · {b.tip_count} tips
                      </div>
                    </div>
                    <div className="font-black text-white whitespace-nowrap">
                      {b.currency} {b.total_dj_share_local.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <BatchStatusPill status={b.status}/>
                      <Button data-testid={`admin-payouts-dl-${b.batch_id}`} variant="outline" size="sm"
                        onClick={() => downloadBatchCsv(b.batch_id)}
                        className="h-7 px-2 border-[#2A354B] text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF]">
                        <Download className="w-3 h-3"/>
                      </Button>
                      {b.status === "pending" && (
                        <>
                          <Button data-testid={`admin-payouts-mark-paid-${b.batch_id}`} size="sm"
                            onClick={() => markBatchPaid(b.batch_id)}
                            disabled={batchBusy === b.batch_id}
                            className="h-7 px-2 bg-[#10B981] text-black hover:bg-[#15C898] text-[10px] font-black uppercase tracking-widest">
                            {batchBusy === b.batch_id ? <Loader2 className="w-3 h-3 animate-spin"/> : "✓ Paid"}
                          </Button>
                          <Button data-testid={`admin-payouts-mark-failed-${b.batch_id}`} variant="outline" size="sm"
                            onClick={() => markBatchFailed(b.batch_id)}
                            disabled={batchBusy === b.batch_id}
                            className="h-7 px-2 border-[#EF4444]/60 text-[#EF4444] hover:bg-[#EF4444]/10 text-[10px] font-black uppercase tracking-widest">
                            Fail
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-[#64748B] font-mono italic">
            // CSV columns match M-Pesa Bulk B2C / MTN MoMo Disbursement / Airtel bulk-pay portals (phone,name,amount,reference,note). Upload to the Safaricom or MTN portal, then click "✓ Paid" to close the batch.
            {" "}Weekly email reminder fires Mondays 09:15 UTC.
          </div>
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-email-test-panel">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#00F0FF]"/>
            <div className="font-display font-black text-2xl">Verify email delivery</div>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Send a test email to any address — Gmail, Yahoo, Hotmail, Live, iCloud, Proton, Yandex, GMX, Outlook, Orange, 163, corporate domains, or anywhere else. Handy for confirming a new sender or debugging a spam-folder issue.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Input
              data-testid="admin-email-test-input"
              type="email"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder="recipient@yahoo.com"
              className="flex-1 min-w-[240px] bg-[#0B0E14] border-[#2A354B] text-white"
            />
            <Button
              data-testid="admin-email-test-send"
              onClick={sendTestEmail}
              disabled={sendingTest}
              className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin"/> : "Send test email"}
            </Button>
          </div>
          <div className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-widest">
            // We relay via {user?.email} → the recipient's inbox. Deliverability depends on the receiver's spam filter.
          </div>
        </div>

        {/* Lifetime PRO — comp account provisioning */}
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-comp-panel">
          <div className="flex items-center gap-3">
            <Gift className="w-5 h-5 text-[#FFC107]"/>
            <div className="font-display font-black text-2xl">Lifetime PRO accounts</div>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Create free-forever accounts for early testers, friends who helped build the idea, or contest winners.
            A random secure password is generated once — copy it, share it via DM, and the recipient can change it after first login.
            If the email already has an account, it's upgraded in place instead of erroring out.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              data-testid="admin-comp-email"
              type="email"
              value={compEmail}
              onChange={e => setCompEmail(e.target.value)}
              placeholder="friend@gmail.com"
              className="bg-[#0B0E14] border-[#2A354B] text-white"
            />
            <Input
              data-testid="admin-comp-name"
              type="text"
              value={compName}
              onChange={e => setCompName(e.target.value)}
              placeholder="Display name (optional)"
              className="bg-[#0B0E14] border-[#2A354B] text-white"
            />
            <Input
              data-testid="admin-comp-reason"
              type="text"
              value={compReason}
              onChange={e => setCompReason(e.target.value)}
              placeholder="Reason (e.g. Early tester)"
              className="bg-[#0B0E14] border-[#2A354B] text-white"
            />
          </div>
          <Button
            data-testid="admin-comp-create"
            onClick={createComp}
            disabled={compBusy || !compEmail}
            className="bg-[#FFC107] text-black hover:bg-[#FFD54F] font-black uppercase tracking-widest w-full sm:w-auto"
          >
            {compBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create Lifetime Account"}
          </Button>

          {/* Password reveal — shown once, right after creation */}
          {compCreated && (
            <div className="rounded-lg border border-[#FFC107]/40 bg-[#FFC107]/5 p-4 space-y-2" data-testid="admin-comp-created">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107]">
                // {compCreated.upgraded ? "Upgraded existing account" : "Account created · copy the password NOW"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest mb-1">Email</div>
                  <div className="flex items-center gap-2 bg-[#0B0E14] rounded px-3 py-2 border border-[#1E2638]">
                    <span className="font-mono text-white text-sm flex-1 truncate" data-testid="admin-comp-created-email">{compCreated.email}</span>
                    <button type="button" onClick={() => copyText(compCreated.email, "comp-email")} className="text-[#94A3B8] hover:text-white transition">
                      {copiedKey === "comp-email" ? <Check className="w-3.5 h-3.5 text-[#10B981]"/> : <Copy className="w-3.5 h-3.5"/>}
                    </button>
                  </div>
                </div>
                {compCreated.password && (
                  <div>
                    <div className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest mb-1">One-time password</div>
                    <div className="flex items-center gap-2 bg-[#0B0E14] rounded px-3 py-2 border border-[#FFC107]/50">
                      <span className="font-mono text-[#FFC107] text-sm flex-1 truncate" data-testid="admin-comp-created-password">{compCreated.password}</span>
                      <button type="button" onClick={() => copyText(compCreated.password, "comp-pass")} className="text-[#94A3B8] hover:text-white transition" data-testid="admin-comp-copy-password">
                        {copiedKey === "comp-pass" ? <Check className="w-3.5 h-3.5 text-[#10B981]"/> : <Copy className="w-3.5 h-3.5"/>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="text-[10.5px] text-[#94A3B8]">{compCreated.message}</div>
              <button type="button" onClick={() => setCompCreated(null)} data-testid="admin-comp-dismiss" className="text-[10px] font-mono text-[#94A3B8] hover:text-white uppercase tracking-widest">Dismiss</button>
            </div>
          )}

          {/* Existing lifetime accounts */}
          <div className="pt-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-2">
              Active lifetime accounts ({compedUsers.length})
            </div>
            {compedUsers.length === 0 ? (
              <div className="text-xs text-[#94A3B8] italic" data-testid="admin-comp-empty">No lifetime accounts yet.</div>
            ) : (
              <div className="space-y-1" data-testid="admin-comp-list">
                {compedUsers.map(u => (
                  <div key={u.id} data-testid={`admin-comp-row-${u.email}`}
                       className="flex items-center gap-2 px-3 py-2 rounded bg-[#0B0E14] border border-[#1E2638] text-[11px]">
                    <Crown className="w-3 h-3 text-[#FFC107] shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-white truncate">{u.email}</div>
                      <div className="text-[10px] text-[#94A3B8] truncate">{u.display_name} · {u.comp_reason || "friend"} · by {u.comp_by_email}</div>
                    </div>
                    <button type="button" onClick={() => revokeComp(u.id, u.email)}
                            data-testid={`admin-comp-revoke-${u.email}`}
                            className="text-[#94A3B8] hover:text-[#EF4444] transition p-1"
                            title="Revoke lifetime status">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Emergency password reset — bypass SMTP */}
        <div className="rounded-xl border border-[#EF4444]/40 bg-[#121620] p-6 space-y-4" data-testid="admin-reset-panel">
          <div className="flex items-center gap-3">
            <KeyRound className="w-5 h-5 text-[#EF4444]"/>
            <div className="font-display font-black text-2xl">Emergency password reset</div>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Generates a fresh 16-char random password for any user and shows it once. Use when a user can't
            receive the reset email (SMTP paused, spam filter, etc). Share the new password privately —
            ask them to change it after first login.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            <Input
              data-testid="admin-reset-email"
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="user@example.com"
              className="bg-[#0B0E14] border-[#2A354B] text-white"
            />
            <Button
              data-testid="admin-reset-submit"
              onClick={doResetPassword}
              disabled={resetBusy || !resetEmail}
              className="bg-[#EF4444] text-white hover:bg-[#DC2626] font-black uppercase tracking-widest"
            >
              {resetBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Reset password"}
            </Button>
          </div>
          {resetResult && (
            <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 space-y-2" data-testid="admin-reset-result">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#EF4444]">
                // Copy the password NOW — it will not be shown again
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest mb-1">Email</div>
                  <div className="flex items-center gap-2 bg-[#0B0E14] rounded px-3 py-2 border border-[#1E2638]">
                    <span className="font-mono text-white text-sm flex-1 truncate" data-testid="admin-reset-result-email">{resetResult.email}</span>
                    <button type="button" onClick={() => copyText(resetResult.email, "reset-email")} className="text-[#94A3B8] hover:text-white transition">
                      {copiedKey === "reset-email" ? <Check className="w-3.5 h-3.5 text-[#10B981]"/> : <Copy className="w-3.5 h-3.5"/>}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#94A3B8] font-mono uppercase tracking-widest mb-1">New password</div>
                  <div className="flex items-center gap-2 bg-[#0B0E14] rounded px-3 py-2 border border-[#EF4444]/60">
                    <span className="font-mono text-[#EF4444] text-sm flex-1 truncate" data-testid="admin-reset-result-password">{resetResult.new_password}</span>
                    <button type="button" onClick={() => copyText(resetResult.new_password, "reset-pass")} data-testid="admin-reset-copy-password" className="text-[#94A3B8] hover:text-white transition">
                      {copiedKey === "reset-pass" ? <Check className="w-3.5 h-3.5 text-[#10B981]"/> : <Copy className="w-3.5 h-3.5"/>}
                    </button>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setResetResult(null)} data-testid="admin-reset-dismiss" className="text-[10px] font-mono text-[#94A3B8] hover:text-white uppercase tracking-widest">Dismiss</button>
            </div>
          )}
        </div>

        {/* Admin reset audit trail — chain-of-custody */}
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-audit-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-[#94A3B8]"/>
              <div className="font-display font-black text-2xl">Emergency reset audit trail</div>
            </div>
            <Button
              data-testid="admin-audit-refresh"
              onClick={loadAuditLog}
              disabled={auditBusy}
              variant="outline"
              className="border-[#2A354B] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-white font-black uppercase tracking-widest text-xs"
            >
              {auditBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Refresh"}
            </Button>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Every emergency password reset is logged here: who reset whom, from what IP, and when.
            Chain-of-custody proof if a user disputes an unexpected password change.
          </p>
          {auditLog?.entries?.length === 0 && (
            <div className="text-xs text-[#94A3B8] italic" data-testid="admin-audit-empty">No admin resets performed yet.</div>
          )}
          {auditLog?.entries?.length > 0 && (
            <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="admin-audit-list">
              {auditLog.entries.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-start px-3 py-2 rounded bg-[#0B0E14] border border-[#1E2638] text-[11px]" data-testid={`admin-audit-row-${i}`}>
                  <div className="min-w-0">
                    <div className="text-[9px] text-[#94A3B8] font-mono uppercase tracking-widest">Admin</div>
                    <div className="font-mono text-white truncate">{a.by_admin_email}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] text-[#94A3B8] font-mono uppercase tracking-widest">Target</div>
                    <div className="font-mono text-[#00F0FF] truncate">{a.target_email}</div>
                  </div>
                  <div className="min-w-0 sm:text-right">
                    <div className="text-[9px] text-[#94A3B8] font-mono uppercase tracking-widest">When · IP</div>
                    <div className="font-mono text-white text-[10px] truncate">{new Date(a.created_at).toLocaleString()}</div>
                    <div className="font-mono text-[#94A3B8] text-[10px] truncate">{a.ip}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-forgot-log-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#00F0FF]"/>
              <div className="font-display font-black text-2xl">Password reset delivery log</div>
            </div>
            <Button
              data-testid="admin-forgot-log-refresh"
              onClick={loadForgotLog}
              disabled={forgotBusy}
              variant="outline"
              className="border-[#2A354B] hover:border-[#00F0FF] hover:text-[#00F0FF] bg-transparent text-white font-black uppercase tracking-widest text-xs"
            >
              {forgotBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Refresh"}
            </Button>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Every "Forgot password" attempt is logged here with the delivery status. If SMTP failed, the reset link is
            captured so you can hand-deliver it. Click Refresh after a user reports missing emails.
          </p>

          {forgotLog?.env && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[10px] font-mono uppercase tracking-widest" data-testid="admin-forgot-log-env">
              <EnvChip label="Outbound paused" ok={!forgotLog.env.email_outbound_paused} good="OFF" bad="ON (blocks all)"/>
              <EnvChip label="SMTP configured" ok={forgotLog.env.smtp_host_set && forgotLog.env.smtp_user_set && forgotLog.env.smtp_password_set} good="YES" bad="MISSING"/>
              <EnvChip label="Turnstile" ok={forgotLog.env.turnstile_secret_set} good="SET" bad="NOT SET"/>
              <EnvChip label="Public origin" ok={!!forgotLog.env.public_app_origin} good={forgotLog.env.public_app_origin || "SET"} bad="NOT SET"/>
              <EnvChip label="Slack alerts" ok={forgotLog.env.slack_webhook_set} good="WEBHOOK SET" bad="NOT SET"/>
            </div>
          )}

          {forgotLog?.attempts?.length === 0 && (
            <div className="text-xs text-[#94A3B8] italic" data-testid="admin-forgot-log-empty">No forgot-password attempts logged yet.</div>
          )}

          {forgotLog?.attempts?.length > 0 && (
            <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="admin-forgot-log-list">
              {forgotLog.attempts.map((a, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr_auto] gap-2 items-start px-3 py-2 rounded bg-[#0B0E14] border border-[#1E2638] text-[11px]" data-testid={`admin-forgot-log-row-${i}`}>
                  <StatusPill status={a.status}/>
                  <div className="min-w-0">
                    <div className="font-mono text-white truncate">{a.email}</div>
                    <div className="text-[10px] text-[#94A3B8]">
                      {new Date(a.created_at).toLocaleString()} · {a.ip}
                      {a.reason && <span className="text-[#EF4444]"> · {a.reason}</span>}
                    </div>
                    {a.reset_link && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#00F0FF] truncate flex-1">{a.reset_link}</span>
                        <button type="button" onClick={() => copyText(a.reset_link, `link-${i}`)} className="text-[#94A3B8] hover:text-white transition shrink-0" data-testid={`admin-forgot-log-copy-${i}`}>
                          {copiedKey === `link-${i}` ? <Check className="w-3 h-3 text-[#10B981]"/> : <Copy className="w-3 h-3"/>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-purge-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Eraser className="w-5 h-5 text-[#F59E0B]"/>
              <div className="font-display font-black text-2xl">Sweep test accounts</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="admin-purge-preview-btn"
                onClick={previewPurge}
                disabled={purgeBusy}
                variant="outline"
                className="border-[#2A354B] hover:border-[#F59E0B] hover:text-[#F59E0B] bg-transparent text-white font-black uppercase tracking-widest text-xs"
              >
                {purgeBusy && !purgePreview ? <Loader2 className="w-4 h-4 animate-spin"/> : "Preview sweep"}
              </Button>
              {purgePreview?.would_delete_count > 0 && (
                <Button
                  data-testid="admin-purge-confirm-btn"
                  onClick={confirmPurge}
                  disabled={purgeBusy}
                  className="bg-[#EF4444] text-white hover:bg-[#DC2626] font-black uppercase tracking-widest text-xs"
                >
                  {purgeBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : `Confirm — delete ${purgePreview.would_delete_count}`}
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-[#94A3B8]">
            One-click cleanup for synthetic accounts left behind by test runs (any email at <code className="font-mono text-[#00F0FF]">@katwiretest.dev / @nd.io / @bücher.de</code> etc., or with a CI-style local-part like <code className="font-mono text-[#00F0FF]">test_ / anti- / gen-</code>). Real admins in <code className="font-mono text-[#00F0FF]">ADMIN_EMAILS</code> and any <code className="font-mono text-[#00F0FF]">is_admin=true</code> account are protected automatically.
          </p>

          {purgePreview && !purgeResult && (
            <div className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4 space-y-2" data-testid="admin-purge-preview">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#F59E0B]">
                Dry-run · {purgePreview.would_delete_count} account{purgePreview.would_delete_count === 1 ? "" : "s"} would be deleted
              </div>
              {purgePreview.would_delete_count > 0 && (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {purgePreview.preview.map((u, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto] gap-2 px-3 py-1.5 rounded bg-[#0B0E14] border border-[#1E2638] text-[11px]" data-testid={`admin-purge-preview-row-${i}`}>
                      <span className="font-mono text-white truncate">{u.email}</span>
                      <span className="font-mono text-[#94A3B8] shrink-0">{u.handle}</span>
                    </div>
                  ))}
                  {purgePreview.would_delete_count > purgePreview.preview.length && (
                    <div className="text-[10px] font-mono text-[#94A3B8] italic px-3">
                      + {purgePreview.would_delete_count - purgePreview.preview.length} more not shown
                    </div>
                  )}
                </div>
              )}
              {purgePreview.would_delete_count === 0 && (
                <div className="text-xs text-[#94A3B8] italic">Nothing to sweep — every account here is real.</div>
              )}
            </div>
          )}

          {purgeResult && (
            <div className="rounded-lg border border-[#10B981]/40 bg-[#10B981]/5 p-4 space-y-2" data-testid="admin-purge-result">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#10B981]">
                Swept · {purgeResult.deleted_users} user{purgeResult.deleted_users === 1 ? "" : "s"} deleted · {purgeResult.users_remaining} real users remain
              </div>
              {Object.keys(purgeResult.deleted_by_collection || {}).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[10px] font-mono">
                  {Object.entries(purgeResult.deleted_by_collection).map(([k, v]) => (
                    <div key={k} className="px-2 py-1 rounded bg-[#0B0E14] border border-[#1E2638] text-[#94A3B8]">
                      <span className="text-[#00F0FF]">{k}</span> <span className="text-white">×{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6 space-y-4" data-testid="admin-midi-telemetry-panel">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sliders className="w-5 h-5 text-[#B6FF3C]"/>
              <div className="font-display font-black text-2xl">MIDI controller telemetry</div>
              {midiTelemetry && (
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]" data-testid="admin-midi-telemetry-total">
                  {midiTelemetry.total} pings · {midiTelemetry.tally?.length || 0} unique models
                </span>
              )}
            </div>
            <Button
              data-testid="admin-midi-telemetry-refresh"
              onClick={loadMidiTelemetry}
              disabled={midiTelemetryBusy}
              variant="outline"
              className="border-[#2A354B] hover:border-[#B6FF3C] hover:text-[#B6FF3C] bg-transparent text-white font-black uppercase tracking-widest text-xs"
            >
              {midiTelemetryBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Refresh"}
            </Button>
          </div>
          <p className="text-sm text-[#94A3B8]">
            Every browser reports its connected controller once. Rows tagged <span className="text-[#F59E0B] font-mono">CATCH-ALL</span> hit
            a generic fallback — good candidates for a dedicated preset. Copy the raw device string when a user reports a mapping bug.
          </p>

          {midiTelemetry?.tally?.length > 0 && (
            <div className="space-y-1" data-testid="admin-midi-telemetry-tally">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1">Top devices seen</div>
              {midiTelemetry.tally.slice(0, 10).map((t, i) => {
                // Only the two true "no dedicated map" presets count as catch-alls.
                // Everything else — including `ddj-sz-clone` — routes to an accurate
                // model-specific mapping.
                const isCatchAll = t.model_id === "pioneer-any-2deck" || t.model_id === "serato-clone-4deck";
                return (
                  <div key={i} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 rounded bg-[#0B0E14] border border-[#1E2638] text-[11px]" data-testid={`admin-midi-telemetry-tally-row-${i}`}>
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border tracking-widest shrink-0 ${isCatchAll ? "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10" : "text-[#B6FF3C] border-[#B6FF3C]/40 bg-[#B6FF3C]/10"}`}>
                      {isCatchAll ? "CATCH-ALL" : "SPECIFIC"}
                    </span>
                    <div className="min-w-0">
                      <div className="font-mono text-white truncate">{t.device_name || "(no name)"}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono truncate">preset: {t.model_id}</div>
                    </div>
                    <span className="font-mono text-[#00F0FF] font-black">×{t.count}</span>
                    <button type="button" onClick={() => copyText(t.device_name || "", `midi-tally-${i}`)}
                            className="text-[#94A3B8] hover:text-white transition shrink-0"
                            data-testid={`admin-midi-telemetry-copy-${i}`}>
                      {copiedKey === `midi-tally-${i}` ? <Check className="w-3 h-3 text-[#10B981]"/> : <Copy className="w-3 h-3"/>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {midiTelemetry?.pings?.length > 0 && (
            <details className="text-[11px]" data-testid="admin-midi-telemetry-details">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] hover:text-white">
                Last {midiTelemetry.pings.length} raw pings
              </summary>
              <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                {midiTelemetry.pings.map((p, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2 rounded bg-[#0B0E14] border border-[#1E2638]" data-testid={`admin-midi-telemetry-ping-${i}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-white truncate">{p.device_name || "(no name)"}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono truncate">
                        {p.model_id} · {p.manufacturer || "?"} · {p.ip}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono text-[#94A3B8] shrink-0 text-right">
                      {p.reported_at ? new Date(p.reported_at).toLocaleString() : ""}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {midiTelemetry && midiTelemetry.total === 0 && (
            <div className="text-xs text-[#94A3B8] italic" data-testid="admin-midi-telemetry-empty">
              No controllers detected yet. Once a user connects hardware in Studio, a single ping lands here.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EnvChip({ label, ok, good, bad }) {
  return (
    <div className={`rounded px-2.5 py-2 border ${ok ? "border-[#10B981]/40 bg-[#10B981]/5 text-[#10B981]" : "border-[#EF4444]/40 bg-[#EF4444]/5 text-[#EF4444]"}`}>
      <div className="text-[9px] opacity-70">{label}</div>
      <div className="text-[11px] font-black truncate">{ok ? good : bad}</div>
    </div>
  );
}

function StatusPill({ status }) {  const map = {
    sent:          { c: "#10B981", t: "SENT" },
    smtp_failed:   { c: "#EF4444", t: "SMTP FAIL" },
    smtp_paused:   { c: "#F59E0B", t: "PAUSED" },
    smtp_missing:  { c: "#F59E0B", t: "NO SMTP" },
    global_capped: { c: "#F59E0B", t: "GLOBAL CAP" },
    blocked:       { c: "#94A3B8", t: "BLOCKED" },
    no_such_user:  { c: "#94A3B8", t: "NO USER" },
  };
  const m = map[status] || { c: "#94A3B8", t: (status || "?").toUpperCase() };
  return (
    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border tracking-widest shrink-0"
          style={{ borderColor: `${m.c}55`, background: `${m.c}15`, color: m.c }}>
      {m.t}
    </span>
  );
}

function BatchStatusPill({ status }) {
  const map = {
    pending: { c: "#F59E0B", t: "PENDING" },
    paid:    { c: "#10B981", t: "PAID" },
    failed:  { c: "#EF4444", t: "FAILED" },
  };
  const m = map[status] || { c: "#94A3B8", t: (status || "?").toUpperCase() };
  return (
    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border tracking-widest shrink-0"
          style={{ borderColor: `${m.c}55`, background: `${m.c}15`, color: m.c }}>
      {m.t}
    </span>
  );
}
